import React, { FC } from 'react';
import { UserInfo } from '../../../common/model/dto/user-info';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptions } from '../../../services/admin/admin-subscription.service';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvPageTitle from '../molecules/OvPageTitle';
import CachedIcon from '@mui/icons-material/Cached';
import queryString from 'query-string';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvSubscriptionList from './OvSubscriptionList';
import OvNoContent from '../molecules/OvNoContent';
import OvErrorMessage from '../atoms/OvErrorMessage';
import { useParams } from 'react-router-dom';

interface OvUserSubscriptionsProps {
  selectedUser?: UserInfo;
}

const OvUserSubscriptions: FC<OvUserSubscriptionsProps> = ({
  selectedUser,
}) => {
  const { t } = useTranslation();
  let { userId }: any = useParams();

  const {
    data: paginatedSubscriptions,
    isPending: isGetUserSubscriptionsPending,
    isFetching: isGetUserSubscriptionsFetching,
    error: getUserSubscriptionsError,
  } = useQuery({
    queryKey: ['admin-user-subscriptions', userId],
    queryFn: () =>
      getSubscriptions(
        queryString.stringify({
          offset: 0,
          limit: 0,
          order_by: 'created_at',
          order_dir: 'desc',
          user_id: userId,
        })
      ),
    retry: 3,
    enabled: !!userId,
  });

  const subscriptions = paginatedSubscriptions?.docs ?? [];
  const total = paginatedSubscriptions?.total ?? 0;

  const isLoading =
    isGetUserSubscriptionsFetching || isGetUserSubscriptionsPending;

  return (
    <Container>
      <CenterPane>
        <HeaderItems>
          <TitleAndSearch>
            <OvPageTitle
              title={t('userDetails.tabs.subscriptions')}
              icon={<CachedIcon />}
            />
          </TitleAndSearch>
        </HeaderItems>

        {getUserSubscriptionsError && (
          <OvErrorMessage message={getUserSubscriptionsError.message} />
        )}

        {subscriptions.length > 0 && (
          <OvSubscriptionList subscriptions={subscriptions} />
        )}

        {total === 0 && !isLoading && (
          <OvNoContent>{t('subscriptions.noContent')}</OvNoContent>
        )}
      </CenterPane>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </Container>
  );
};

export default OvUserSubscriptions;

const Container = styled.div`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const HeaderItems = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const TitleAndSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
